import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';
import { deferAfterPaint } from '../lib/deferAfterPaint';
import { playNotificationSound } from '../lib/notificationSound';
import { dumpSyncPhaseLabel, type DumpSyncPhase } from './gtinDumpSyncLabels';

export type { DumpSyncPhase };
export { dumpSyncPhaseLabel };

export type DumpSyncState = {
  running: boolean;
  startedAt: string | null;
  phase: DumpSyncPhase | null;
  processedRows: number | null;
  totalRows: number | null;
  createdRows: number | null;
  updatedRows: number | null;
  lastError: string | null;
  lastFinishedAt: string | null;
  lastUpserted: number | null;
  lastCreated: number | null;
  lastUpdated: number | null;
};

export type TrackedDumpSync = DumpSyncState & {
  /** Stable key for the current/last run (startedAt). */
  runId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
};

export const GTIN_DUMP_SYNC_STARTED_EVENT = 'wapharma-gtin-dump-sync-started';
export const GTIN_DUMP_SYNC_FINISHED_EVENT = 'wapharma-gtin-dump-sync-finished';
export const GTIN_DUMP_SYNC_STORAGE_KEY = 'hamssah.gtin-dump-sync.tracking';

const POLL_MS = 1000;

function readTracking(): boolean {
  try {
    return localStorage.getItem(GTIN_DUMP_SYNC_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeTracking(on: boolean) {
  try {
    if (on) localStorage.setItem(GTIN_DUMP_SYNC_STORAGE_KEY, '1');
    else localStorage.removeItem(GTIN_DUMP_SYNC_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function mapState(raw: DumpSyncState): TrackedDumpSync | null {
  const runId = raw.startedAt ?? raw.lastFinishedAt;
  if (!runId) return null;
  if (raw.running) {
    return { ...raw, runId, status: 'RUNNING' };
  }
  if (raw.lastError) {
    return { ...raw, runId, status: 'FAILED' };
  }
  if (raw.lastFinishedAt) {
    return { ...raw, runId, status: 'COMPLETED' };
  }
  return null;
}

/**
 * Polls OBF dump sync progress for the notification flyout (survives navigation).
 * Same pattern as `useCatalogImportJobInbox` — no BullMQ; cheap in-memory status endpoint.
 */
export function useGtinDumpSyncInbox() {
  const token = getToken();
  const { showToast } = useToast();
  const [job, setJob] = useState<TrackedDumpSync | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const notifiedRef = useRef<Set<string>>(new Set());
  const pollEnabledRef = useRef(readTracking());
  const seenResultRef = useRef<Set<string>>(new Set());

  const dismissResult = useCallback(() => {
    if (!job?.runId) return;
    seenResultRef.current.add(job.runId);
    notifiedRef.current.add(job.runId);
    setJob(null);
    setErr(null);
    writeTracking(false);
  }, [job?.runId]);

  useEffect(() => {
    if (!token) {
      setJob(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startPolling = () => {
      if (timer || document.hidden) return;
      timer = setInterval(() => void poll(), POLL_MS);
    };

    const poll = async () => {
      if (!pollEnabledRef.current) return;
      try {
        const raw = await apiGet<{ data: DumpSyncState } | DumpSyncState>(
          '/catalog/admin/external-catalog/dump-sync',
          token,
        );
        const state = unwrapData(raw) as DumpSyncState;
        if (cancelled) return;

        const tracked = mapState(state);
        if (!tracked) {
          setJob(null);
          setErr(null);
          stopPolling();
          writeTracking(false);
          return;
        }

        if (tracked.status !== 'RUNNING' && seenResultRef.current.has(tracked.runId)) {
          setJob(null);
          setErr(null);
          stopPolling();
          writeTracking(false);
          return;
        }

        setJob(tracked);
        setErr(null);

        if (tracked.status === 'RUNNING') {
          writeTracking(true);
          startPolling();
          return;
        }

        // Terminal
        stopPolling();
        writeTracking(false);

        if (!notifiedRef.current.has(tracked.runId)) {
          notifiedRef.current.add(tracked.runId);
          playNotificationSound();
          if (tracked.status === 'COMPLETED') {
            const n = tracked.lastUpserted ?? 0;
            const detail =
              tracked.lastCreated != null && tracked.lastUpdated != null
                ? ` (${tracked.lastCreated.toLocaleString('fr-FR')} nouveaux · ${tracked.lastUpdated.toLocaleString('fr-FR')} mis à jour)`
                : '';
            showToast(`Dump ingéré · ${n.toLocaleString('fr-FR')} GTIN${detail}`, {
              tone: 'success',
            });
          } else {
            showToast(tracked.lastError ?? 'Sync dump échoué.', { tone: 'error' });
          }
          window.dispatchEvent(
            new CustomEvent(GTIN_DUMP_SYNC_FINISHED_EVENT, { detail: tracked }),
          );
          window.dispatchEvent(new Event('wapharma-inbox-reload'));
        }
      } catch (e: unknown) {
        if (cancelled) return;
        setErr(getDisplayError(e));
        stopPolling();
      }
    };

    const onStarted = () => {
      pollEnabledRef.current = true;
      writeTracking(true);
      void poll();
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopPolling();
        return;
      }
      if (!pollEnabledRef.current) return;
      void poll();
    };

    const cancelDefer = deferAfterPaint(() => {
      if (!cancelled && pollEnabledRef.current) void poll();
    });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(GTIN_DUMP_SYNC_STARTED_EVENT, onStarted);
    return () => {
      cancelled = true;
      cancelDefer();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(GTIN_DUMP_SYNC_STARTED_EVENT, onStarted);
    };
  }, [token, showToast]);

  const inFlight = Boolean(job && job.status === 'RUNNING');
  const showResult = Boolean(job && (job.status === 'COMPLETED' || job.status === 'FAILED'));

  return {
    job,
    err,
    inFlight,
    showResult,
    dismissResult,
    importProgress:
      job && job.status === 'RUNNING'
        ? {
            current: job.processedRows ?? 0,
            total: job.totalRows && job.totalRows > 0 ? job.totalRows : Math.max(job.processedRows ?? 0, 1),
            phase: job.phase,
            indeterminate: job.phase !== 'ingesting' || job.totalRows == null,
            created: job.createdRows,
            updated: job.updatedRows,
          }
        : null,
  };
}
